// Editor / Detalhe de prova agendada — full view (não modal).
//
// Substitui o modal antigo de criação/edição. Permite, em uma única página:
//   - Configurar: título, disciplina (offering), janela, duração, lobby,
//                 banco fonte, instruções, subject, nota de corte, visibilidade
//                 pra aluno, política de release.
//   - Pesos & Questões: lista com peso editável e preview de cada questão.
//   - Cockpit: enquanto live (heartbeat dos alunos, progresso).
//   - Resultados: pós-prova com ranking, distribuição, e ANULAR questão
//                 retroativamente (recalcula notas + notifica alunos).
//
// Modos:
//   - sem id → Criar nova prova (mostra só Configuração + Pesos quando banco selecionado)
//   - com id → Editar / monitorar (mostra abas conforme fase)

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock,
  Eye,
  EyeOff,
  Flag,
  Info,
  Lightbulb,
  Loader2,
  Pencil,
  Save,
  Settings2,
  Trash2,
  Users,
  XCircle
} from "lucide-react";
import {
  api,
  ApiError,
  type BankRow,
  type ExamAnswers,
  type ExamConfigFull,
  type ExamDetail,
  type ExamMonitor
} from "../api";
import { Switch } from "@app/ui/components/switch";
import { openConfirm, openPrompt, openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

// =====================
// Estado / routing simples (sem URL própria — sinaliza pela ProvasView)
// =====================

export type DetailMode = "create" | "edit";
export interface DetailTarget {
  mode: DetailMode;
  examId?: string;
}

export const provaDetailTarget = signal<DetailTarget | null>(null);

type Tab = "config" | "questoes" | "cockpit" | "resultados" | "historico";

// =====================
// Helpers
// =====================

function phaseOf(exam: { startsAt: string | null; endsAt: string | null; releasedAt: string | null }):
  | "lobby"
  | "live"
  | "closed"
  | "released" {
  const now = Date.now();
  const s = exam.startsAt ? Date.parse(exam.startsAt) : Number.POSITIVE_INFINITY;
  const e = exam.endsAt ? Date.parse(exam.endsAt) : Number.NEGATIVE_INFINITY;
  if (s > now) return "lobby";
  if (e >= now) return "live";
  if (exam.releasedAt) return "released";
  return "closed";
}

function toLocalDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

// =====================
// Componente principal
// =====================

export function ProvaAgendadaDetailView({
  target,
  onClose,
  onRefreshList
}: {
  target: DetailTarget;
  onClose: () => void;
  onRefreshList: () => void;
}) {
  const isCreating = target.mode === "create";
  const [phase, setPhase] = useState<"loading" | "ready" | "error">(
    isCreating ? "ready" : "loading"
  );
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [exam, setExam] = useState<ExamDetail | null>(null);
  const [banks, setBanks] = useState<BankRow[]>([]);
  const [tab, setTab] = useState<Tab>("config");

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const tasks: Array<Promise<unknown>> = [api.listBanks()];
      if (target.examId) tasks.push(api.getExam(target.examId));
      const results = await Promise.all(tasks);
      const br = results[0] as { banks: BankRow[] };
      setBanks(br.banks.filter((b) => !b.archivedAt));
      if (target.examId) {
        const er = results[1] as { exam: ExamDetail };
        setExam(er.exam);
        const p = phaseOf(er.exam);
        // Default tab depende da fase
        if (p === "live") setTab("cockpit");
        else if (p === "closed" || p === "released") setTab("resultados");
        else setTab("config");
      }
      setPhase("ready");
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        window.location.href = "/auth/";
        return;
      }
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, [target.examId, target.mode]);

  if (phase === "loading") {
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg}
      </div>
    );
  }

  const p = exam ? phaseOf(exam) : "lobby";
  const tabs: Array<{ key: Tab; label: string; icon: any }> = [
    { key: "config", label: "Configuração", icon: Settings2 },
    { key: "questoes", label: "Pesos e questões", icon: ClipboardList }
  ];
  if (exam) {
    if (p === "live" || p === "lobby") {
      tabs.push({ key: "cockpit", label: "Cockpit", icon: Users });
    }
    if (p === "closed" || p === "released" || p === "live") {
      tabs.push({ key: "resultados", label: "Resultados", icon: Eye });
    }
    tabs.push({ key: "historico", label: "Histórico", icon: Clock });
  }

  return (
    <div class="space-y-4">
      <DetailHeader
        exam={exam}
        isCreating={isCreating}
        onClose={onClose}
      />

      <nav class="flex flex-wrap items-center gap-1 border-b border-border">
        {tabs.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            icon={t.icon}
            label={t.label}
            onClick={() => setTab(t.key)}
          />
        ))}
      </nav>

      {tab === "config" && (
        <ConfigTab
          exam={exam}
          banks={banks}
          isCreating={isCreating}
          onSaved={async (id) => {
            await load();
            onRefreshList();
            if (isCreating && id) {
              provaDetailTarget.value = { mode: "edit", examId: id };
            }
          }}
        />
      )}
      {tab === "questoes" && exam && (
        <QuestoesTab exam={exam} onChange={() => void load()} />
      )}
      {tab === "questoes" && !exam && (
        <div class="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          Salve a configuração básica primeiro pra editar pesos por questão.
        </div>
      )}
      {tab === "cockpit" && exam && <CockpitTab exam={exam} />}
      {tab === "resultados" && exam && (
        <ResultadosTab exam={exam} onChange={() => void load()} />
      )}
      {tab === "historico" && exam && <HistoricoTab exam={exam} />}
    </div>
  );
}

// =====================
// Header
// =====================

function DetailHeader({
  exam,
  isCreating,
  onClose
}: {
  exam: ExamDetail | null;
  isCreating: boolean;
  onClose: () => void;
}) {
  return (
    <header class="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
      <div class="min-w-0 flex-1 space-y-1">
        <button
          type="button"
          onClick={onClose}
          class="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3" /> Voltar para a lista
        </button>
        <h1 class="text-xl font-bold tracking-tight">
          {isCreating ? "Nova prova agendada" : exam?.title ?? ""}
        </h1>
        {exam && (
          <div class="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            {exam.offering && (
              <span class="inline-flex items-center gap-1">
                <ClipboardList className="size-3" />
                {exam.offering.disciplinaName} · {exam.offering.turmaName}
              </span>
            )}
            {exam.startsAt && (
              <span class="inline-flex items-center gap-1">
                <Calendar className="size-3" />
                {new Date(exam.startsAt).toLocaleString("pt-BR")}
              </span>
            )}
            {exam.locks.isLocked && (
              <span class="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 font-semibold text-amber-700 dark:text-amber-300">
                <Info className="size-3" /> Edição parcialmente travada
              </span>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

function TabButton({
  active,
  icon: Icon,
  label,
  onClick
}: {
  active: boolean;
  icon: any;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold transition-colors ${
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );
}

// =====================
// Tab: Configuração
// =====================

interface OfferingOption {
  offeringId: string;
  disciplinaId: string;
  disciplinaName: string;
  turmaName: string;
}

function ConfigTab({
  exam,
  banks,
  isCreating,
  onSaved
}: {
  exam: ExamDetail | null;
  banks: BankRow[];
  isCreating: boolean;
  onSaved: (id?: string) => void | Promise<void>;
}) {
  const cfg: ExamConfigFull = exam?.config ?? {};
  const [title, setTitle] = useState(exam?.title ?? "");
  const [bankId, setBankId] = useState(exam?.snapshot.bankId ?? "");
  const [examMode, setExamMode] = useState<"live" | "open_window">(
    cfg.examMode ?? "live"
  );
  const [studentDurationMin, setStudentDurationMin] = useState<number>(
    cfg.studentDurationMin ?? 60
  );
  const [allowPause, setAllowPause] = useState<boolean>(cfg.allowPause ?? false);
  const [offeringId, setOfferingId] = useState<string>(exam?.offeringId ?? "");
  const [evaluationItemId, setEvaluationItemId] = useState<string>(
    exam?.evaluationItem?.id ?? ""
  );
  const [createEvalItem, setCreateEvalItem] = useState(false);
  const [newEvalWeight, setNewEvalWeight] = useState<number>(2);
  const [offerings, setOfferings] = useState<OfferingOption[]>([]);
  const [evalItems, setEvalItems] = useState<
    Array<{ id: string; title: string; weight: number; assessmentId: string | null }>
  >([]);
  const [startsAt, setStartsAt] = useState(toLocalDateTime(exam?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(toLocalDateTime(exam?.endsAt ?? null));
  const [durationMinutes, setDurationMinutes] = useState<string>(
    exam?.durationMinutes != null ? String(exam.durationMinutes) : ""
  );
  const [subject, setSubject] = useState(cfg.subject ?? "");
  const [instructions, setInstructions] = useState(cfg.instructions ?? "");
  const [hallLeadMin, setHallLeadMin] = useState<number>(cfg.hallLeadMin ?? 15);
  const [passingGrade, setPassingGrade] = useState<number>(cfg.passingGrade ?? 6);
  const [showQuestionCount, setShowQuestionCount] = useState<boolean>(
    cfg.showQuestionCountToStudent ?? true
  );
  const [showWeights, setShowWeights] = useState<boolean>(
    cfg.showWeightsToStudent ?? true
  );
  const [autoRelease, setAutoRelease] = useState<boolean>(
    cfg.autoReleaseResults ?? true
  );
  const [releaseMode, setReleaseMode] = useState<"after_end" | "fixed">(
    cfg.resultReleaseMode ?? "after_end"
  );
  const [releaseOffset, setReleaseOffset] = useState<number>(
    cfg.resultReleaseOffsetMin ?? 30
  );
  const [releaseAt, setReleaseAt] = useState<string>(
    toLocalDateTime(cfg.resultReleaseAt ?? null)
  );
  const [disclosure, setDisclosure] = useState<"full" | "score">(
    cfg.resultDisclosureLevel ?? "full"
  );
  const [releasedNow, setReleasedNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const p = exam ? phaseOf(exam) : "lobby";
  const locked = exam?.locks.isLocked ?? false;
  const editableWindow = !locked;

  // Carrega disciplinas/offerings do prof (1x na montagem)
  useEffect(() => {
    let canceled = false;
    async function loadOfferings() {
      try {
        const r = await api.listDisciplinas();
        if (canceled) return;
        // pra cada disciplina, busca detail (offerings)
        const allOffs: OfferingOption[] = [];
        for (const d of r.disciplinas) {
          try {
            const det = await api.getDisciplina(d.id);
            for (const off of det.disciplina.offerings) {
              allOffs.push({
                offeringId: off.id,
                disciplinaId: d.id,
                disciplinaName: d.name,
                turmaName: off.turmaName
              });
            }
          } catch {}
        }
        if (!canceled) setOfferings(allOffs);
      } catch {}
    }
    void loadOfferings();
    return () => {
      canceled = true;
    };
  }, []);

  // Quando offering muda, carrega items de avaliação da disciplina dela
  useEffect(() => {
    if (!offeringId) {
      setEvalItems([]);
      return;
    }
    const off = offerings.find((o) => o.offeringId === offeringId);
    if (!off) return;
    let canceled = false;
    (async () => {
      try {
        const r = await api.getDisciplinaEvaluation(off.disciplinaId);
        if (canceled) return;
        setEvalItems(
          r.items.map((i) => ({
            id: i.id,
            title: i.title,
            weight: i.weight,
            assessmentId: i.assessmentId
          }))
        );
      } catch {
        if (!canceled) setEvalItems([]);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [offeringId, offerings.length]);

  async function save() {
    setSaving(true);
    setErrMsg(null);
    try {
      const payload: any = {
        title: title.trim(),
        examMode,
        studentDurationMin,
        allowPause,
        subject: subject.trim() || null,
        instructions: instructions.trim() || null,
        hallLeadMin,
        passingGrade,
        showQuestionCountToStudent: showQuestionCount,
        showWeightsToStudent: showWeights,
        autoReleaseResults: autoRelease,
        resultReleaseMode: releaseMode,
        resultReleaseOffsetMin: releaseOffset,
        resultReleaseAt:
          releaseMode === "fixed" && releaseAt
            ? new Date(releaseAt).toISOString()
            : null,
        resultDisclosureLevel: disclosure,
        showDisclosureToStudent: true
      };
      if (editableWindow) {
        if (startsAt) payload.startsAt = new Date(startsAt).toISOString();
        if (endsAt) payload.endsAt = new Date(endsAt).toISOString();
      }
      if (durationMinutes) payload.durationMinutes = Number(durationMinutes);
      if (releasedNow) payload.releasedAt = new Date().toISOString();
      payload.offeringId = offeringId || null;

      // Vínculo com item de avaliação (bidirecional)
      if (createEvalItem && offeringId) {
        const off = offerings.find((o) => o.offeringId === offeringId);
        if (off) {
          payload.createEvaluationItem = {
            disciplinaId: off.disciplinaId,
            weight: newEvalWeight,
            title: title.trim()
          };
        }
      } else if (evaluationItemId) {
        payload.evaluationItemId = evaluationItemId;
      } else if (!isCreating) {
        // Mandar null explícito desvincula
        payload.evaluationItemId = null;
      }

      if (isCreating) {
        if (!bankId) throw new Error("Selecione um banco fonte.");
        payload.bankId = bankId;
        const r = await api.createExam(payload);
        await onSaved(r.exam.id);
      } else if (exam) {
        await api.patchExam(exam.id, payload);
        await onSaved(exam.id);
      }
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="space-y-4">
      {errMsg && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mr-1 inline size-3.5" />
          {errMsg}
        </div>
      )}

      <SectionCard title="Identificação" icon={ClipboardList}>
        <Field label="Título *">
          <input
            type="text"
            value={title}
            onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Matéria/tema (mostrado pro aluno)">
          <input
            type="text"
            value={subject}
            placeholder="Ex: Farmacologia clínica"
            onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        {isCreating ? (
          <Field label="Banco fonte *">
            <select
              value={bankId}
              onChange={(e) => setBankId((e.target as HTMLSelectElement).value)}
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            >
              <option value="">— Escolha um banco —</option>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.questionCount} questões)
                </option>
              ))}
            </select>
            <p class="mt-1 text-[10px] text-muted-foreground">
              Snapshot do banco é tirado no início da prova.
            </p>
          </Field>
        ) : (
          exam?.snapshot.bankName && (
            <Field label="Banco fonte">
              <div class="rounded-md border border-border bg-muted/30 px-3 py-1.5 text-sm">
                {exam.snapshot.bankName}{" "}
                <span class="text-muted-foreground">
                  ({exam.snapshot.bankCount} questões)
                </span>
              </div>
            </Field>
          )
        )}
      </SectionCard>

      <SectionCard title="Tipo de prova" icon={Calendar}>
        <div class="flex flex-wrap gap-2">
          <RadioCard
            active={examMode === "live"}
            onClick={() => !locked && setExamMode("live")}
            title="Sala ao vivo"
            desc="Todos os alunos começam no mesmo horário (Início). Cronômetro único pra turma. Ideal pra provas presenciais ou síncronas."
          />
          <RadioCard
            active={examMode === "open_window"}
            onClick={() => !locked && setExamMode("open_window")}
            title="Período aberto"
            desc="Aluno escolhe quando começar dentro de [Início, Fim]. Tem um tempo individual a partir do momento que iniciar. Pode ou não permitir pausa."
          />
        </div>
        {examMode === "open_window" && (
          <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Tempo individual após começar (min) *">
              <input
                type="number"
                min="1"
                max="1440"
                value={studentDurationMin}
                disabled={locked}
                onInput={(e) =>
                  setStudentDurationMin(Number((e.target as HTMLInputElement).value))
                }
                class="w-32 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-60"
              />
              <p class="mt-1 text-[10px] text-muted-foreground">
                Quanto tempo cada aluno tem desde o momento que iniciar a prova.
              </p>
            </Field>
            <Field label="Pausar e retomar">
              <ToggleField
                label="Permitir aluno pausar"
                hint="Se ligado, o aluno pode pausar e retomar. O cronômetro só corre quando ele está ativo. (Mantém o limite da janela [Início, Fim])"
                checked={allowPause}
                onChange={setAllowPause}
              />
            </Field>
          </div>
        )}
        {examMode === "live" && (
          <p class="mt-2 rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
            <Info className="mr-1 inline size-3" />
            Lobby abre {cfg.hallLeadMin ?? 15} min antes do início. Todos os alunos
            entram na sala ao mesmo tempo e veem um cronômetro único que fecha em Fim.
          </p>
        )}
      </SectionCard>

      <SectionCard title="Vínculo com disciplina e avaliação" icon={Flag}>
        <Field label="Disciplina × Turma (offering)">
          <select
            value={offeringId}
            disabled={locked}
            onChange={(e) => {
              setOfferingId((e.target as HTMLSelectElement).value);
              setEvaluationItemId("");
              setCreateEvalItem(false);
            }}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-60"
          >
            <option value="">— Sem vínculo (prova avulsa) —</option>
            {offerings.map((o) => (
              <option key={o.offeringId} value={o.offeringId}>
                {o.disciplinaName} — {o.turmaName}
              </option>
            ))}
          </select>
          <p class="mt-1 text-[10px] text-muted-foreground">
            Os alunos matriculados nesta turma serão avisados. Sem vínculo, a prova
            funciona como simulada.
          </p>
        </Field>
        {offeringId && (
          <Field label="Avaliação composta da disciplina">
            <div class="space-y-2">
              <label class="flex items-center gap-2 text-xs">
                <input
                  type="radio"
                  name="evallink"
                  checked={!createEvalItem && !evaluationItemId}
                  onChange={() => {
                    setCreateEvalItem(false);
                    setEvaluationItemId("");
                  }}
                />
                <span>Não vincular agora (posso vincular depois pela disciplina)</span>
              </label>
              {evalItems.length > 0 && (
                <label class="flex items-center gap-2 text-xs">
                  <input
                    type="radio"
                    name="evallink"
                    checked={!createEvalItem && !!evaluationItemId}
                    onChange={() => setCreateEvalItem(false)}
                  />
                  <span>Vincular a um item existente:</span>
                </label>
              )}
              {!createEvalItem && evalItems.length > 0 && (
                <select
                  value={evaluationItemId}
                  onChange={(e) =>
                    setEvaluationItemId((e.target as HTMLSelectElement).value)
                  }
                  class="ml-6 w-[calc(100%-1.5rem)] rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                >
                  <option value="">— Escolha o item —</option>
                  {evalItems
                    .filter((i) => !i.assessmentId || i.assessmentId === exam?.id)
                    .map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.title} (peso {i.weight.toFixed(2)})
                      </option>
                    ))}
                </select>
              )}
              <label class="flex items-center gap-2 text-xs">
                <input
                  type="radio"
                  name="evallink"
                  checked={createEvalItem}
                  onChange={() => {
                    setCreateEvalItem(true);
                    setEvaluationItemId("");
                  }}
                />
                <span>Criar item novo na disciplina:</span>
              </label>
              {createEvalItem && (
                <div class="ml-6 flex items-center gap-2 text-xs">
                  <span class="text-muted-foreground">Peso:</span>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.5"
                    value={newEvalWeight}
                    onInput={(e) =>
                      setNewEvalWeight(Number((e.target as HTMLInputElement).value))
                    }
                    class="w-20 rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
                  />
                  <span class="text-muted-foreground">de 10 da disciplina</span>
                </div>
              )}
            </div>
          </Field>
        )}
        {exam?.evaluationItem && (
          <p class="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
            <Flag className="mr-1 inline size-3" />
            Esta prova é o item <strong>{exam.evaluationItem.title}</strong> da avaliação composta
            (peso {exam.evaluationItem.weight.toFixed(2)}).
          </p>
        )}
      </SectionCard>

      <SectionCard title="Janela de execução" icon={Clock}>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={`Início ${editableWindow ? "*" : "(travado)"}`}>
            <input
              type="datetime-local"
              value={startsAt}
              disabled={!editableWindow}
              onInput={(e) => setStartsAt((e.target as HTMLInputElement).value)}
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-60"
            />
          </Field>
          <Field label={`Fim ${editableWindow ? "*" : "(travado)"}`}>
            <input
              type="datetime-local"
              value={endsAt}
              disabled={!editableWindow}
              onInput={(e) => setEndsAt((e.target as HTMLInputElement).value)}
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-60"
            />
          </Field>
          <Field label="Duração máxima (min) — opcional">
            <input
              type="number"
              value={durationMinutes}
              min="0"
              placeholder="Ex: 60"
              onInput={(e) => setDurationMinutes((e.target as HTMLInputElement).value)}
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </Field>
          <Field label={`Lobby (min antes do início) ${editableWindow ? "" : "(travado)"}`}>
            <input
              type="number"
              value={hallLeadMin}
              min="0"
              max="60"
              disabled={!editableWindow}
              onInput={(e) => setHallLeadMin(Number((e.target as HTMLInputElement).value))}
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-60"
            />
            <p class="mt-1 text-[10px] text-muted-foreground">
              Antes desse intervalo o aluno só vê o "anúncio". A partir daí, vê o lobby
              com countdown.
            </p>
          </Field>
        </div>
        {locked && (
          <p class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
            <Info className="mr-1 inline size-3" />
            O lobby do aluno já abriu — datas, banco, título, peso e horário do lobby
            estão travados.
          </p>
        )}
      </SectionCard>

      <SectionCard title="Instruções e nota" icon={Lightbulb}>
        <Field label="Instruções (visíveis pro aluno antes e durante)">
          <textarea
            value={instructions}
            onInput={(e) => setInstructions((e.target as HTMLTextAreaElement).value)}
            rows={4}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Nota mínima de aprovação">
          <div class="flex items-center gap-3">
            <input
              type="number"
              value={passingGrade}
              min="0"
              max="10"
              step="0.5"
              onInput={(e) => setPassingGrade(Number((e.target as HTMLInputElement).value))}
              class="w-24 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
            <span class="text-[11px] text-muted-foreground">de 10 pontos possíveis</span>
          </div>
        </Field>
      </SectionCard>

      <SectionCard title="Visibilidade pro aluno" icon={Eye}>
        <ToggleField
          label="Mostrar quantidade total de questões"
          hint="Aluno vê 'X questões' no lobby"
          checked={showQuestionCount}
          onChange={setShowQuestionCount}
        />
        <ToggleField
          label="Mostrar peso de cada questão"
          hint="Badge 'Vale X pts' aparece em cada questão durante a prova"
          checked={showWeights}
          onChange={setShowWeights}
        />
      </SectionCard>

      <SectionCard title="Liberação de resultado" icon={Flag}>
        <ToggleField
          label="Liberar resultado automaticamente"
          hint="Sem isso, você libera manualmente quando quiser"
          checked={autoRelease}
          onChange={setAutoRelease}
        />
        {autoRelease && (
          <>
            <Field label="Modo de liberação">
              <div class="flex flex-wrap gap-2">
                <RadioCard
                  active={releaseMode === "after_end"}
                  onClick={() => setReleaseMode("after_end")}
                  title="Após o fim"
                  desc="N minutos depois do encerramento"
                />
                <RadioCard
                  active={releaseMode === "fixed"}
                  onClick={() => setReleaseMode("fixed")}
                  title="Horário cravado"
                  desc="Data/hora específica (>= fim da prova)"
                />
              </div>
            </Field>
            {releaseMode === "after_end" ? (
              <Field label="Liberar quantos minutos após o fim">
                <input
                  type="number"
                  value={releaseOffset}
                  min="0"
                  max="1440"
                  onInput={(e) =>
                    setReleaseOffset(Number((e.target as HTMLInputElement).value))
                  }
                  class="w-32 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </Field>
            ) : (
              <Field label="Liberar exatamente em">
                <input
                  type="datetime-local"
                  value={releaseAt}
                  onInput={(e) => setReleaseAt((e.target as HTMLInputElement).value)}
                  class="rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </Field>
            )}
          </>
        )}
        <Field label="O que o aluno vê quando liberado">
          <div class="flex flex-wrap gap-2">
            <RadioCard
              active={disclosure === "full"}
              onClick={() => setDisclosure("full")}
              title="Tudo"
              desc="Nota + gabarito completo (revisão questão-a-questão)"
            />
            <RadioCard
              active={disclosure === "score"}
              onClick={() => setDisclosure("score")}
              title="Só nota"
              desc="Sem gabarito (aluno não revê questões)"
            />
          </div>
        </Field>
        {p === "closed" && exam && (
          <label class="flex items-center gap-2 rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-xs">
            <Switch
              checked={releasedNow}
              onCheckedChange={(v) => setReleasedNow(v)}
            />
            <span class="font-semibold">Liberar resultados AGORA (override manual)</span>
          </label>
        )}
      </SectionCard>

      <div class="flex justify-end gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !title.trim() || (isCreating && !bankId)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save width="1em" height="1em" />
          )}
          {isCreating ? "Criar prova" : "Salvar alterações"}
        </button>
      </div>
    </div>
  );
}

// =====================
// Tab: Pesos e questões
// =====================

function QuestoesTab({
  exam,
  onChange
}: {
  exam: ExamDetail;
  onChange: () => void;
}) {
  const [questoes, setQuestoes] = useState<any[] | null>(null);
  const [weights, setWeights] = useState<number[]>(exam.weights ?? []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    let canceled = false;
    async function loadQs() {
      setLoading(true);
      try {
        // 2 caminhos: pega do bank original (antes do snapshot) OU do exam-answers
        // (que retorna snapshot.questoes após startsAt).
        const p = phaseOf(exam);
        if (p === "lobby") {
          if (!exam.snapshot.bankId) {
            if (!canceled) setQuestoes([]);
            return;
          }
          const r = await api.getBank(exam.snapshot.bankId);
          const qs = (r.bank.payload as any)?.questoes ?? [];
          if (!canceled) setQuestoes(qs);
        } else {
          const r = await api.examAnswers(exam.id);
          if (!canceled) setQuestoes(r.questoes);
        }
      } catch (e) {
        if (!canceled) setErrMsg(friendlyError(e));
      } finally {
        if (!canceled) setLoading(false);
      }
    }
    void loadQs();
    return () => {
      canceled = true;
    };
  }, [exam.id]);

  const total = questoes?.length ?? 0;
  const somaPesos = weights.reduce((a, b) => a + b, 0);
  const somaOk = Math.abs(somaPesos - 10) < 0.01;

  function setWeight(idx: number, v: number) {
    const next = [...weights];
    next[idx] = Math.max(0, Math.round(v * 100) / 100);
    setWeights(next);
  }

  function distribuirIgualmente() {
    if (total === 0) return;
    const w = Math.round((10 / total) * 100) / 100;
    const arr = Array.from({ length: total }, () => w);
    const sum = arr.reduce((a, b) => a + b, 0);
    arr[0] = Math.round((arr[0] + (10 - sum)) * 100) / 100;
    setWeights(arr);
  }

  async function savePesos() {
    setSaving(true);
    setErrMsg(null);
    try {
      await api.patchExam(exam.id, { weights });
      onChange();
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando questões…
      </p>
    );
  }
  if (errMsg) {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
        <AlertCircle className="mr-1 inline size-3.5" />
        {errMsg}
      </div>
    );
  }
  if (!questoes || questoes.length === 0) {
    return (
      <div class="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
        Sem questões disponíveis no banco.
      </div>
    );
  }

  return (
    <div class="space-y-3">
      <header class="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="text-xs">
          Soma dos pesos:{" "}
          <strong
            class={`tabular-nums ${
              somaOk ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
            }`}
          >
            {somaPesos.toFixed(2)}
          </strong>{" "}
          / <span class="text-muted-foreground">10.00</span>
          {!somaOk && (
            <span class="ml-2 text-[10px] text-amber-700 dark:text-amber-400">
              (precisa fechar em 10)
            </span>
          )}
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            onClick={distribuirIgualmente}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-[11px] font-semibold hover:bg-accent"
          >
            Distribuir igualmente
          </button>
          <button
            type="button"
            onClick={savePesos}
            disabled={saving || !somaOk || exam.locks.isLocked}
            title={exam.locks.isLocked ? "Pesos travados — lobby já abriu" : ""}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[11px] font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <Save width="0.9em" height="0.9em" />
            )}
            Salvar pesos
          </button>
        </div>
      </header>

      <ul class="space-y-2">
        {questoes.map((q, i) => {
          const isOpen = expanded.has(i);
          return (
            <li
              key={q?.id ?? i}
              class="rounded-md border border-border bg-card/40"
            >
              <div class="flex items-center gap-3 px-3 py-2">
                <span class="grid size-7 shrink-0 place-items-center rounded-md border border-border bg-background text-xs font-bold tabular-nums">
                  {i + 1}
                </span>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-xs font-medium">
                    {(typeof q?.pergunta === "string"
                      ? q.pergunta
                      : q?.enunciado ?? ""
                    ).slice(0, 120)}
                  </p>
                  <p class="truncate text-[10px] text-muted-foreground">
                    {q?.tema ? `${q.tema} · ` : ""}{q?.fonte ?? ""}
                  </p>
                </div>
                <label class="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Peso
                  <input
                    type="number"
                    value={weights[i] ?? 0}
                    step="0.1"
                    min="0"
                    disabled={exam.locks.isLocked}
                    onInput={(e) =>
                      setWeight(i, Number((e.target as HTMLInputElement).value))
                    }
                    class="w-16 rounded-md border border-border bg-background px-2 py-1 text-right text-xs tabular-nums outline-none focus:border-primary disabled:opacity-60"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const next = new Set(expanded);
                    if (next.has(i)) next.delete(i);
                    else next.add(i);
                    setExpanded(next);
                  }}
                  class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                  title={isOpen ? "Recolher" : "Visualizar questão"}
                >
                  {isOpen ? (
                    <EyeOff className="size-3.5" />
                  ) : (
                    <Eye className="size-3.5" />
                  )}
                </button>
              </div>
              {isOpen && (
                <div class="border-t border-border px-4 py-3 text-xs">
                  <QuestionPreview q={q} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function QuestionPreview({ q }: { q: any }) {
  const enunciado = typeof q?.pergunta === "string" ? q.pergunta : q?.enunciado ?? "";
  const opcoes: string[] = Array.isArray(q?.opcoes)
    ? q.opcoes
    : Array.isArray(q?.alternativas)
      ? q.alternativas
      : [];
  const correta = typeof q?.correta === "number" ? q.correta - 1 : -1;
  return (
    <div class="space-y-2">
      {q?.caso && (
        <div class="rounded-md border-l-4 border-sky-500/50 bg-sky-500/5 px-3 py-2">
          <span class="block text-[10px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            Situação
          </span>
          <div class="mt-1 whitespace-pre-wrap leading-relaxed">{q.caso}</div>
        </div>
      )}
      <div class="whitespace-pre-wrap leading-relaxed">{enunciado}</div>
      <ol class="space-y-1">
        {opcoes.map((opt, j) => {
          const letter = String.fromCharCode(65 + j);
          const isCorr = j === correta;
          return (
            <li
              key={j}
              class={`flex items-start gap-2 rounded-md border px-2 py-1.5 ${
                isCorr
                  ? "border-emerald-500/40 bg-emerald-500/10"
                  : "border-border bg-background/30"
              }`}
            >
              <span
                class={`grid size-5 shrink-0 place-items-center rounded-full text-[10px] font-bold ${
                  isCorr
                    ? "bg-emerald-500 text-white"
                    : "bg-muted text-foreground"
                }`}
              >
                {letter}
              </span>
              <span class="flex-1 leading-relaxed">{opt}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// =====================
// Tab: Cockpit (live)
// =====================

function CockpitTab({ exam }: { exam: ExamDetail }) {
  const [data, setData] = useState<ExamMonitor | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [announceMsg, setAnnounceMsg] = useState("");
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [extendMin, setExtendMin] = useState<number>(10);
  const [showExtend, setShowExtend] = useState(false);

  async function refresh() {
    try {
      const r = await api.examMonitor(exam.id);
      setData(r);
      setErrMsg(null);
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 10_000);
    return () => clearInterval(t);
  }, [exam.id]);

  async function announceAction() {
    if (!announceMsg.trim()) return;
    setActionBusy("announce");
    try {
      await api.announceExam(exam.id, announceMsg.trim());
      setAnnounceMsg("");
      setShowAnnounce(false);
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setActionBusy(null);
    }
  }
  async function extendAction() {
    if (extendMin <= 0) return;
    setActionBusy("extend");
    try {
      await api.extendExam(exam.id, { addMinutes: extendMin });
      setShowExtend(false);
      setExtendMin(10);
      // recarrega exam superior — usaremos page reload simples (cockpit lê via prop)
      window.location.reload();
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setActionBusy(null);
    }
  }
  async function forceEndAction() {
    const ok = await openConfirm({
      title: "Encerrar a prova agora?",
      message: "Todas as respostas atuais dos alunos serão salvas e ninguém poderá continuar respondendo. Essa ação não pode ser desfeita.",
      confirmLabel: "Encerrar agora",
      danger: true
    });
    if (!ok) return;
    setActionBusy("force_end");
    try {
      await api.forceEndExam(exam.id);
      window.location.reload();
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setActionBusy(null);
    }
  }
  async function resetAttemptAction(userId: string, name: string) {
    const reason = await openPrompt({
      title: `Reabrir tentativa de ${name}?`,
      message: "Isso APAGA as respostas atuais do aluno nessa prova. Use só em casos de falha técnica ou pedido legítimo.",
      placeholder: "Motivo (opcional, registrado no log)",
      confirmLabel: "Reabrir tentativa",
      danger: true,
      multiline: true,
      maxLength: 400
    });
    if (reason === null) return;
    setActionBusy(`reset-${userId}`);
    try {
      await api.resetAttempt(exam.id, userId, reason || undefined);
      await refresh();
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setActionBusy(null);
    }
  }

  if (loading && !data) {
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando cockpit…
      </p>
    );
  }
  if (errMsg) {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
        <AlertCircle className="mr-1 inline size-3.5" />
        {errMsg}
      </div>
    );
  }
  if (!data) return null;

  const phase = phaseOf(exam);
  const canCockpitActions = phase === "live" || phase === "lobby";

  return (
    <div class="space-y-3">
      {canCockpitActions && (
        <section class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
          <div class="mb-2 flex flex-wrap items-center gap-2">
            <span class="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              Ações de cockpit
            </span>
            <button
              type="button"
              onClick={() => setShowAnnounce((s) => !s)}
              disabled={actionBusy !== null}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent disabled:opacity-50"
            >
              Avisar a turma
            </button>
            {phase === "live" && (
              <>
                <button
                  type="button"
                  onClick={() => setShowExtend((s) => !s)}
                  disabled={actionBusy !== null}
                  class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent disabled:opacity-50"
                >
                  Estender prazo
                </button>
                <button
                  type="button"
                  onClick={() => void forceEndAction()}
                  disabled={actionBusy !== null}
                  class="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] font-semibold text-destructive hover:bg-destructive/20 disabled:opacity-50"
                >
                  Encerrar agora
                </button>
              </>
            )}
          </div>
          {showAnnounce && (
            <div class="flex flex-col gap-2 sm:flex-row">
              <textarea
                value={announceMsg}
                onInput={(e) => setAnnounceMsg((e.target as HTMLTextAreaElement).value)}
                rows={2}
                maxLength={500}
                placeholder="Mensagem que vai pra todos os alunos (até 500 chars)…"
                class="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
              <button
                type="button"
                onClick={() => void announceAction()}
                disabled={actionBusy === "announce" || !announceMsg.trim()}
                class="inline-flex items-center justify-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
              >
                {actionBusy === "announce" ? <Loader2 className="size-3 animate-spin" /> : "Enviar"}
              </button>
            </div>
          )}
          {showExtend && (
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-[11px] text-muted-foreground">Acrescentar</span>
              <input
                type="number"
                min="1"
                max="1440"
                value={extendMin}
                onInput={(e) => setExtendMin(Number((e.target as HTMLInputElement).value))}
                class="w-20 rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
              />
              <span class="text-[11px] text-muted-foreground">minutos ao fim atual</span>
              <button
                type="button"
                onClick={() => void extendAction()}
                disabled={actionBusy === "extend" || extendMin <= 0}
                class="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
              >
                {actionBusy === "extend" ? <Loader2 className="size-3 animate-spin" /> : "Estender"}
              </button>
            </div>
          )}
        </section>
      )}

      <section class="grid gap-3 sm:grid-cols-4">
        <KpiCard
          label="Matriculados"
          value={String(data.summary.totalMembros)}
          color="muted"
        />
        <KpiCard
          label="Conectaram"
          value={String(data.summary.conectaram)}
          color="primary"
        />
        <KpiCard
          label="Online agora"
          value={String(data.summary.onlineAgora)}
          color="emerald"
        />
        <KpiCard
          label="Finalizaram"
          value={String(data.summary.finalizaram)}
          color="violet"
        />
      </section>

      <div class="rounded-md border border-border bg-card/40">
        <table class="w-full text-xs">
          <thead class="bg-muted/30 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th class="px-3 py-2 text-left">Aluno</th>
              <th class="px-3 py-2 text-left">Status</th>
              <th class="px-3 py-2 text-left">Entrou</th>
              <th class="px-3 py-2 text-left">Último ping</th>
              <th class="px-3 py-2 text-right">Respondidas</th>
              <th class="px-3 py-2 text-right">Nota</th>
              <th class="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border">
            {data.students.map((s) => (
              <tr key={s.userId} class="hover:bg-accent/40">
                <td class="px-3 py-2">
                  <div class="font-semibold">{s.name || s.apelido || "—"}</div>
                  <div class="text-[10px] text-muted-foreground">{s.email}</div>
                </td>
                <td class="px-3 py-2">
                  {s.submittedAt ? (
                    <span class="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold text-violet-700 dark:text-violet-300">
                      <CheckCircle2 className="size-3" /> Finalizou
                    </span>
                  ) : s.online ? (
                    <span class="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
                      <span class="size-1.5 animate-pulse rounded-full bg-emerald-500" />
                      Online
                    </span>
                  ) : s.startedAt ? (
                    <span class="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
                      Pausado
                    </span>
                  ) : (
                    <span class="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
                      Não entrou
                    </span>
                  )}
                </td>
                <td class="px-3 py-2 text-[11px] tabular-nums text-muted-foreground">
                  {s.startedAt
                    ? new Date(s.startedAt).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit"
                      })
                    : "—"}
                </td>
                <td class="px-3 py-2 text-[11px] tabular-nums text-muted-foreground">
                  {s.lastSeenAt
                    ? new Date(s.lastSeenAt).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit"
                      })
                    : "—"}
                </td>
                <td class="px-3 py-2 text-right tabular-nums">{s.answeredCount}</td>
                <td class="px-3 py-2 text-right font-bold tabular-nums">
                  {s.score != null ? Number(s.score).toFixed(2) : "—"}
                </td>
                <td class="px-3 py-2 text-right">
                  {s.startedAt && (
                    <button
                      type="button"
                      onClick={() => void resetAttemptAction(s.userId, s.name || s.email)}
                      disabled={actionBusy === `reset-${s.userId}`}
                      class="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] font-semibold text-amber-700 hover:bg-amber-500/10 dark:text-amber-300 disabled:opacity-50"
                      title="Reabrir tentativa do aluno (apaga as respostas atuais)"
                    >
                      {actionBusy === `reset-${s.userId}` ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <>Reabrir</>
                      )}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p class="text-[10px] text-muted-foreground">
        Atualiza automaticamente a cada 10 segundos. Heartbeat dos alunos: 30s.
      </p>
    </div>
  );
}

// =====================
// Tab: Resultados (pós-prova)
// =====================

function ResultadosTab({
  exam,
  onChange
}: {
  exam: ExamDetail;
  onChange: () => void;
}) {
  const [data, setData] = useState<ExamAnswers | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [anulating, setAnulating] = useState<number | null>(null);
  const [openQuestao, setOpenQuestao] = useState<number | null>(null);
  const [openAluno, setOpenAluno] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.examAnswers(exam.id);
      setData(r);
      setErrMsg(null);
    } catch (e) {
      setErrMsg(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [exam.id]);

  async function toggleAnular(idx: number, unanular: boolean) {
    if (anulating != null) return;
    if (
      !confirm(
        unanular
          ? `Desfazer anulação da questão ${idx + 1}? Notas voltam ao valor original.`
          : `Anular a questão ${idx + 1}? Todos os alunos que estavam errando ela ganham os pontos; os que acertaram não mudam. Alunos com nota alterada serão notificados automaticamente.`
      )
    )
      return;
    setAnulating(idx);
    try {
      const r = await api.anularQuestao(exam.id, idx, unanular);
      // Toast simples
      void openAlert({
        title: "Anulação aplicada",
        message: `Pronto. ${r.attemptsUpdated} aluno(s) tiveram a nota recalculada (de ${r.attemptsTotal} no total).`
      });
      await refresh();
      onChange();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setAnulating(null);
    }
  }

  if (loading && !data) {
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando respostas…
      </p>
    );
  }
  if (errMsg) {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
        <AlertCircle className="mr-1 inline size-3.5" />
        {errMsg}
      </div>
    );
  }
  if (!data) return null;

  const studentsSorted = [...data.students].sort((a, b) => b.scoreOf10 - a.scoreOf10);
  const totalStudents = data.students.length;
  const submitted = data.students.filter((s) => s.submittedAt).length;
  const avg =
    submitted > 0
      ? data.students.reduce((s, x) => s + x.scoreOf10, 0) / submitted
      : 0;

  return (
    <div class="space-y-4">
      <ReleaseBanner exam={exam} onChange={onChange} />
      <section class="grid gap-3 sm:grid-cols-4">
        <KpiCard
          label="Alunos submetidos"
          value={`${submitted}/${totalStudents}`}
          color="primary"
        />
        <KpiCard
          label="Média da turma"
          value={avg.toFixed(2)}
          color="violet"
        />
        <KpiCard
          label="Maior nota"
          value={
            studentsSorted.length > 0 ? studentsSorted[0].scoreOf10.toFixed(2) : "—"
          }
          color="emerald"
        />
        <KpiCard
          label="Menor nota"
          value={
            studentsSorted.length > 0
              ? studentsSorted[studentsSorted.length - 1].scoreOf10.toFixed(2)
              : "—"
          }
          color="red"
        />
      </section>

      {/* Ranking de alunos (clicável → expande com gabarito do aluno) */}
      <SectionCard title="Ranking de alunos" icon={Users}>
        <ul class="space-y-2">
          {studentsSorted.map((s, i) => {
            const isOpen = openAluno === s.userId;
            return (
              <li
                key={s.userId}
                class="overflow-hidden rounded-md border border-border bg-card/40"
              >
                <button
                  type="button"
                  onClick={() => setOpenAluno(isOpen ? null : s.userId)}
                  class="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent/40"
                >
                  <span class="grid size-7 shrink-0 place-items-center rounded-md border border-border bg-background text-xs font-bold tabular-nums">
                    {i + 1}º
                  </span>
                  <div class="min-w-0 flex-1">
                    <div class="text-sm font-semibold">{s.name}</div>
                    <div class="text-[10px] text-muted-foreground">{s.email}</div>
                  </div>
                  <div class="flex items-center gap-3 text-[11px] text-muted-foreground">
                    <span>
                      <strong class="tabular-nums text-foreground">
                        {s.acertos}/{data.perQuestion.length}
                      </strong>{" "}
                      acertos
                    </span>
                    <span class="rounded-md bg-primary/10 px-2 py-1 text-sm font-black tabular-nums text-primary">
                      {s.scoreOf10.toFixed(2)}
                    </span>
                    <ChevronRight
                      className={`size-4 shrink-0 text-muted-foreground transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  </div>
                </button>
                {isOpen && (
                  <StudentAnswersDetail
                    student={s}
                    data={data}
                    onPickQuestao={(idx) => {
                      setOpenQuestao(idx);
                      // Scroll suave pra seção
                      setTimeout(() => {
                        const el = document.getElementById(`pq-${idx}`);
                        el?.scrollIntoView({ behavior: "smooth", block: "center" });
                      }, 50);
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </SectionCard>

      {/* Distribuição por questão (clicável → expande preview + anular) */}
      <SectionCard title="Distribuição por questão" icon={ClipboardList}>
        <ul class="space-y-2">
          {data.perQuestion.map((pq) => {
            const isOpen = openQuestao === pq.idx;
            return (
              <li
                id={`pq-${pq.idx}`}
                key={pq.idx}
                class={`overflow-hidden rounded-md border ${
                  pq.isAnulada
                    ? "border-amber-500/40 bg-amber-500/5"
                    : isOpen
                      ? "border-primary/50 bg-primary/5"
                      : "border-border bg-card/40"
                }`}
              >
                <div class="flex items-center gap-3 px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => setOpenQuestao(isOpen ? null : pq.idx)}
                    class="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <span class="grid size-7 shrink-0 place-items-center rounded-md border border-border bg-background text-xs font-bold tabular-nums">
                      {pq.idx + 1}
                    </span>
                    <div class="min-w-0 flex-1">
                      <p
                        class={`text-xs font-medium ${isOpen ? "" : "truncate"}`}
                      >
                        {pq.enunciado.slice(0, isOpen ? 1000 : 120)}
                        {!isOpen && pq.enunciado.length > 120 && "…"}
                      </p>
                      <div class="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                        {pq.peso != null && <span>Peso {pq.peso.toFixed(2)}</span>}
                        <span>•</span>
                        <span>
                          {pq.acertos}/{pq.totalRespostas} acertos
                          {pq.pctAcerto != null && ` (${pq.pctAcerto}%)`}
                        </span>
                        <span>•</span>
                        <span>Correta: {String.fromCharCode(65 + pq.correctIdx)}</span>
                        {pq.isAnulada && (
                          <span class="rounded bg-amber-500/20 px-1.5 py-0.5 font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                            Anulada
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight
                      className={`size-4 shrink-0 text-muted-foreground transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleAnular(pq.idx, pq.isAnulada)}
                    disabled={anulating === pq.idx}
                    class={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      pq.isAnulada
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
                        : "border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-300"
                    } disabled:opacity-50`}
                  >
                    {anulating === pq.idx ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : pq.isAnulada ? (
                      <CheckCircle2 className="size-3" />
                    ) : (
                      <XCircle className="size-3" />
                    )}
                    {pq.isAnulada ? "Desfazer" : "Anular"}
                  </button>
                </div>
                {isOpen && (
                  <QuestaoExpanded pq={pq} questao={data.questoes[pq.idx]} />
                )}
              </li>
            );
          })}
        </ul>
      </SectionCard>
    </div>
  );
}

// =====================
// Expansão: detalhe das respostas de um aluno (gabarito pessoal)
// =====================

function StudentAnswersDetail({
  student,
  data,
  onPickQuestao
}: {
  student: ExamAnswers["students"][number];
  data: ExamAnswers;
  onPickQuestao: (idx: number) => void;
}) {
  // Reconstrói as respostas do aluno a partir do perQuestion.distribuicao.
  // Cada respondente é {userId, name, tempoMs}: encontramos a letra em que
  // o student aparece e usamos o tempoMs dele direto.
  const respostas = data.perQuestion.map((pq) => {
    let opcao: number | null = null;
    let tempoMs: number | null = null;
    for (const [letra, info] of Object.entries(pq.distribuicao)) {
      const hit = info.respondentes.find((r) => r.userId === student.userId);
      if (hit) {
        opcao = letra.charCodeAt(0) - 65;
        tempoMs = hit.tempoMs;
        break;
      }
    }
    const correta = pq.correctIdx;
    let status: "acerto" | "erro" | "anulada" | "pendente";
    if (pq.isAnulada) status = "anulada";
    else if (opcao == null) status = "pendente";
    else if (opcao === correta) status = "acerto";
    else status = "erro";
    return { idx: pq.idx, peso: pq.peso, opcao, correta, status, tempoMs, pq };
  });
  const totalTempoMs = respostas.reduce(
    (sum, r) => sum + (r.tempoMs ?? 0),
    0
  );
  const respondidas = respostas.filter((r) => r.opcao != null).length;
  const tempoMedioMs = respondidas > 0 ? Math.round(totalTempoMs / respondidas) : 0;

  return (
    <div class="border-t border-border bg-background/40 px-4 py-3 text-xs">
      <div class="mb-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>
          Iniciado{" "}
          {student.startedAt
            ? new Date(student.startedAt).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit"
              })
            : "—"}
          {" · "}
          Submetido{" "}
          {student.submittedAt
            ? new Date(student.submittedAt).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit"
              })
            : "—"}
        </span>
        <span class="inline-flex items-center gap-2 tabular-nums">
          <span>
            Tempo total: <strong>{fmtHMS(totalTempoMs)}</strong>
          </span>
          <span>
            Médio/q: <strong>{fmtHMS(tempoMedioMs)}</strong>
          </span>
        </span>
      </div>
      <div class="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {respostas.map((r) => {
          const tone =
            r.status === "acerto"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : r.status === "erro"
                ? "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400"
                : r.status === "anulada"
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                  : "border-border bg-card/40 text-muted-foreground";
          return (
            <button
              key={r.idx}
              type="button"
              onClick={() => onPickQuestao(r.idx)}
              class={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left transition-all hover:scale-[1.01] ${tone}`}
            >
              <span class="flex min-w-0 flex-1 items-center gap-1.5">
                <span class="font-mono font-bold tabular-nums">Q{r.idx + 1}</span>
                <span class="text-[10px] opacity-80">
                  {r.status === "anulada"
                    ? "anulada"
                    : r.opcao != null
                      ? `marcou ${String.fromCharCode(65 + r.opcao)} · gabarito ${String.fromCharCode(
                          65 + r.correta
                        )}`
                      : "em branco"}
                </span>
              </span>
              {r.tempoMs != null && r.tempoMs > 0 && (
                <span class="shrink-0 text-[10px] font-semibold tabular-nums opacity-80">
                  {fmtHMS(r.tempoMs)}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p class="mt-2 text-[10px] italic text-muted-foreground">
        Clique numa questão pra abrir a prévia logo abaixo.
      </p>
    </div>
  );
}

function fmtHMS(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`;
}

// =====================
// Expansão: preview da questão + distribuição visual de respostas
// =====================

function QuestaoExpanded({
  pq,
  questao
}: {
  pq: ExamAnswers["perQuestion"][number];
  questao: any;
}) {
  const opcoesFromSnap: string[] =
    questao && Array.isArray(questao.opcoes)
      ? questao.opcoes
      : pq.alternativas ?? [];
  const correta = pq.correctIdx;
  // Imagens podem vir como array de strings OU de {url, legenda}
  const imagens: Array<{ url: string; legenda?: string }> = [];
  if (typeof questao?.imagem_url === "string" && questao.imagem_url) {
    imagens.push({ url: questao.imagem_url });
  }
  if (Array.isArray(questao?.imagens)) {
    for (const it of questao.imagens) {
      if (typeof it === "string") imagens.push({ url: it });
      else if (it && typeof it === "object" && typeof it.url === "string") {
        imagens.push({ url: it.url, legenda: it.legenda });
      }
    }
  }
  const tabelas: Array<any> = Array.isArray(questao?.tabelas) ? questao.tabelas : [];
  return (
    <div class="border-t border-border bg-background/40 px-4 py-4 text-xs">
      {/* Caso (situação) */}
      {questao?.caso && (
        <div class="mb-3 rounded-md border-l-4 border-sky-500/50 bg-sky-500/5 px-3 py-2">
          <span class="block text-[10px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            Situação
          </span>
          <div class="mt-1 whitespace-pre-wrap leading-relaxed">{questao.caso}</div>
        </div>
      )}
      {/* Tabelas */}
      {tabelas.length > 0 && (
        <div class="mb-3 space-y-2">
          {tabelas.map((t, i) => (
            <SimpleQuestionTable key={i} table={t} />
          ))}
        </div>
      )}
      {/* Imagens */}
      {imagens.length > 0 && (
        <div
          class={`mb-3 grid gap-2 ${
            imagens.length === 1 ? "max-w-md mx-auto" : "max-w-2xl mx-auto sm:grid-cols-2"
          }`}
        >
          {imagens.map((im, i) => (
            <figure
              key={i}
              class="overflow-hidden rounded-md border border-border bg-background/40"
            >
              <img
                src={im.url}
                alt={im.legenda ?? `Imagem ${i + 1}`}
                class="mx-auto block w-full"
                loading="lazy"
              />
              {im.legenda && (
                <figcaption class="border-t border-border bg-card/60 px-3 py-1.5 text-center text-[10px] italic text-muted-foreground">
                  {im.legenda}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
      {/* Enunciado */}
      <div class="mb-3 whitespace-pre-wrap text-sm leading-relaxed">
        {pq.enunciado}
      </div>
      <ol class="space-y-1.5">
        {opcoesFromSnap.map((opt, j) => {
          const letra = String.fromCharCode(65 + j);
          const isCorr = j === correta;
          const dist = pq.distribuicao[letra];
          const count = dist?.count ?? 0;
          const pct = pq.totalRespostas
            ? Math.round((count / pq.totalRespostas) * 100)
            : 0;
          return (
            <li
              key={j}
              class={`relative overflow-hidden rounded-md border ${
                isCorr
                  ? "border-emerald-500/50 bg-emerald-500/10"
                  : "border-border bg-card/40"
              }`}
            >
              <div
                class={`absolute inset-y-0 left-0 ${
                  isCorr ? "bg-emerald-500/15" : "bg-primary/10"
                }`}
                style={{ width: `${pct}%` }}
              />
              <div class="relative flex items-center gap-2 px-3 py-2">
                <span
                  class={`grid size-6 shrink-0 place-items-center rounded-full text-[10px] font-bold ${
                    isCorr ? "bg-emerald-500 text-white" : "bg-muted text-foreground"
                  }`}
                >
                  {letra}
                </span>
                <span class="min-w-0 flex-1 leading-relaxed">{opt}</span>
                <span class="shrink-0 text-[10px] font-bold tabular-nums">
                  {count}{" "}
                  <span class="font-normal text-muted-foreground">({pct}%)</span>
                </span>
              </div>
              {dist && dist.respondentes.length > 0 && (
                <div class="relative border-t border-border/60 px-3 py-1.5 text-[10px] text-muted-foreground">
                  {dist.respondentes
                    .slice(0, 6)
                    .map(
                      (r) =>
                        `${r.name}${r.tempoMs != null && r.tempoMs > 0 ? ` (${fmtHMS(r.tempoMs)})` : ""}`
                    )
                    .join(", ")}
                  {dist.respondentes.length > 6 &&
                    ` e mais ${dist.respondentes.length - 6}`}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// Tabela compacta sem prose styling pesado — mostra título, cabeçalho,
// linhas e nota. Aceita o shape `{titulo, cabecalho, linhas, nota}` do banco.
function SimpleQuestionTable({ table }: { table: any }) {
  if (!table || typeof table !== "object") return null;
  const cab: string[] = Array.isArray(table.cabecalho) ? table.cabecalho : [];
  const linhas: any[][] = Array.isArray(table.linhas) ? table.linhas : [];
  return (
    <div class="overflow-hidden rounded-md border border-border bg-background/60">
      {table.titulo && (
        <div class="border-b border-border bg-muted/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {table.titulo}
        </div>
      )}
      <div class="overflow-x-auto">
        <table class="w-full text-[11px]">
          {cab.length > 0 && (
            <thead class="bg-muted/30 font-semibold">
              <tr>
                {cab.map((c, i) => (
                  <th key={i} class="border-b border-border px-2 py-1 text-left">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody class="divide-y divide-border">
            {linhas.map((row, i) => (
              <tr key={i}>
                {Array.isArray(row) &&
                  row.map((cell, j) => (
                    <td key={j} class="px-2 py-1">
                      {typeof cell === "string" ? cell : String(cell ?? "")}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.nota && (
        <div class="border-t border-border bg-muted/20 px-3 py-1.5 text-[10px] italic text-muted-foreground">
          {table.nota}
        </div>
      )}
    </div>
  );
}

// =====================
// Subcomponents utilitários
// =====================

function SectionCard({
  title,
  icon: Icon,
  children
}: {
  title: string;
  icon: any;
  children: any;
}) {
  return (
    <section class="space-y-3 rounded-md border border-border bg-card/40 px-4 py-4">
      <h2 class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-primary">
        <Icon className="size-3.5" />
        {title}
      </h2>
      <div class="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function ToggleField({
  label,
  hint,
  checked,
  onChange
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label class="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-background/40 px-3 py-2 hover:bg-accent/40">
      <Switch
        checked={checked}
        onCheckedChange={(v) => onChange(v)}
        className="mt-0.5"
      />
      <span class="min-w-0 flex-1">
        <span class="block text-sm font-semibold">{label}</span>
        {hint && <span class="block text-[11px] text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}

function RadioCard({
  active,
  onClick,
  title,
  desc
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex-1 rounded-md border px-3 py-2 text-left transition-colors ${
        active
          ? "border-primary/60 bg-primary/10"
          : "border-border bg-background/40 hover:bg-accent/40"
      }`}
    >
      <span class="block text-sm font-bold">{title}</span>
      <span class="block text-[11px] text-muted-foreground">{desc}</span>
    </button>
  );
}

function KpiCard({
  label,
  value,
  color
}: {
  label: string;
  value: string;
  color: "muted" | "primary" | "emerald" | "violet" | "red";
}) {
  const tone =
    color === "primary"
      ? "border-primary/40 bg-primary/5 text-primary"
      : color === "emerald"
        ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
        : color === "violet"
          ? "border-violet-500/40 bg-violet-500/5 text-violet-700 dark:text-violet-300"
          : color === "red"
            ? "border-red-500/40 bg-red-500/5 text-red-600 dark:text-red-400"
            : "border-border bg-card/40 text-foreground";
  return (
    <div class={`rounded-md border px-3 py-2.5 ${tone}`}>
      <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">
        {label}
      </div>
      <div class="text-xl font-black tabular-nums">{value}</div>
    </div>
  );
}

// =====================
// Banner de liberação manual (Resultados)
// =====================

function ReleaseBanner({
  exam,
  onChange
}: {
  exam: ExamDetail;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const phase = phaseOf(exam);
  const released = phase === "released";

  async function release() {
    const ok = await openConfirm({
      title: "Liberar o resultado agora?",
      message: "Todos os alunos vão receber notificação e poderão ver gabarito, suas respostas e nota. Essa ação não pode ser desfeita.",
      confirmLabel: "Liberar para todos"
    });
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await api.releaseExam(exam.id);
      onChange();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  if (released) {
    return (
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-xs text-violet-700 dark:text-violet-300">
        <CheckCircle2 className="size-4" />
        <span class="font-semibold">Resultado liberado pra turma</span>
        {exam.releasedAt && (
          <span class="text-[10px] opacity-80">
            em {new Date(exam.releasedAt).toLocaleString("pt-BR")}
          </span>
        )}
      </div>
    );
  }
  if (phase !== "closed") return null;
  const effRelease = exam.locks.effectiveReleaseAt;
  return (
    <div class="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs">
      <div class="flex items-center gap-2">
        <Flag className="size-4 text-amber-600" />
        <div>
          <div class="font-bold text-amber-800 dark:text-amber-200">Resultado ainda não liberado</div>
          {effRelease ? (
            <div class="text-[10px] text-muted-foreground">
              Auto-release agendado pra {new Date(effRelease).toLocaleString("pt-BR")}.
            </div>
          ) : (
            <div class="text-[10px] text-muted-foreground">
              Auto-release está desligado — você precisa liberar manualmente.
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => void release()}
        disabled={busy}
        class="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
      >
        {busy ? <Loader2 className="size-3 animate-spin" /> : <Flag className="size-3" />}
        Liberar agora
      </button>
      {err && (
        <p class="w-full text-[10px] text-destructive">{err}</p>
      )}
    </div>
  );
}

// =====================
// Tab: Histórico (audit log)
// =====================

function HistoricoTab({ exam }: { exam: ExamDetail }) {
  const log = exam.auditLog ?? [];
  if (log.length === 0) {
    return (
      <div class="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
        Nenhuma ação registrada ainda.
      </div>
    );
  }
  return (
    <ul class="space-y-1.5">
      {[...log].reverse().map((e) => (
        <li
          key={e.id}
          class="flex items-start gap-2 rounded-md border border-border bg-card/40 px-3 py-2 text-xs"
        >
          <span class="mt-0.5 size-2 shrink-0 rounded-full bg-primary/60" />
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-baseline gap-2">
              <strong class="font-bold">{formatAuditAction(e.action)}</strong>
              <span class="text-[10px] text-muted-foreground">
                {new Date(e.createdAt).toLocaleString("pt-BR")}
              </span>
            </div>
            {Object.keys(e.details).length > 0 && (
              <pre class="mt-1 max-w-full overflow-x-auto rounded-sm bg-muted/40 px-2 py-1 text-[10px] font-mono leading-snug">
                {JSON.stringify(e.details, null, 0)}
              </pre>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatAuditAction(a: string): string {
  switch (a) {
    case "published": return "Prova publicada";
    case "voided_question": return "Questão anulada";
    case "unvoided_question": return "Anulação revertida";
    case "extended_window": return "Prazo estendido";
    case "force_ended": return "Prova encerrada antecipadamente";
    case "released_result": return "Resultado liberado";
    case "reset_attempt": return "Tentativa de aluno reaberta";
    case "announcement": return "Aviso enviado pra turma";
    case "updated_config": return "Configuração alterada";
    case "linked_evaluation_item": return "Vinculado a item de avaliação";
    case "unlinked_evaluation_item": return "Desvinculado de item de avaliação";
    default: return a;
  }
}
